import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { dayjs, utils, occupancyText, occupancyOverrides } from 'passiv-libs'
import { CPI, HUM } from 'passiv-api'

import {
  getInitialUntilTime,
  convertTimeToUTCDate,
  getTipModalText,
  getMobiscrollValue
} from './utils'

const capability = utils.account.hasCapability('immersionhotwater')
  ? 'immersionhotwater'
  : 'heating'

// const zone = utils.account.hasCapability('multizone') ? 1 : 1

const InOutContainer = ({ render }) => {
  const store = window.__STORE__
  const { heating } = store

  const [loading, setLoading] = useState(false)
  const [tipModalInOpen, setTipModalInOpen] = useState(false)
  const [tipModalOutOpen, setTipModalOutOpen] = useState(false)
  const [tipModalAsleepOpen, setTipModalAsleepOpen] = useState(false)
  const [timeModalOpen, setTimeModalOpen] = useState(false)
  // NB untilTime: zulu as sent to server. set first on open, then on scroll
  const [untilTime, setUntilTime] = useState('')

  useEffect(() => {
    const [hours, mins] = getMobiscrollValue({ occupancy }).split(' ')
    const _untilTime = dayjs().hour(hours).minute(mins).utc().format()
    setUntilTime(_untilTime)
  }, [])

  const allHeatingOccupancies = utils.heating.getAllHeatingOccupancies({
    zones: store.zones,
    heating
  })

  const occupancy = utils.account.hasCapability('multizone')
    ? occupancyOverrides.mz.getWholeHomeOccupancy(allHeatingOccupancies)
    : heating.zone1.occupancy

  const putOccupancyOverride = async ({ targetOccupancy, targetUntilTime }) => {
    setLoading(true)

    const calls = []

    // multizone
    if (utils.account.hasCapability('multizone')) {
      if (targetOccupancy === 'IN') {
        const _calls = store.zones
          .filter(({ type }) => type === 'heating')
          .map(({ id }) =>
            CPI.PUT({
              capability,
              zone: id,
              property: 'occupancy_override',
              payload: { occupancy: 'IN', until_always: false }
            })
          )
        calls.push(_calls)
      }
      if (targetOccupancy === 'OUT' && targetUntilTime) {
        const _calls = store.zones
          .filter(({ type }) => type === 'heating')
          .map(({ id }) =>
            CPI.PUT({
              capability,
              zone: id,
              property: 'occupancy_override',
              payload: { occupancy: 'OUT', until_time: targetUntilTime }
            })
          )
        calls.push(_calls)
      }
      // singlezone
    } else {
      if (targetOccupancy === 'IN') {
        calls.push(
          CPI.PUT({
            capability,
            zone: 1,
            property: 'occupancy_override',
            payload: { occupancy: 'IN', until_always: false }
          })
        )
      }
      if (targetOccupancy === 'OUT' && targetUntilTime) {
        calls.push(
          CPI.PUT({
            capability,
            zone: 1,
            property: 'occupancy_override',
            payload: { occupancy: 'OUT', until_time: targetUntilTime }
          })
        )
      }
    }

    try {
      await Promise.all(calls)
      if (utils.settings.canShowAppRateModal()) utils.general.triggerRateApp()
    } catch (error) {}

    setLoading(false)
  }

  // NOTE: tipId must have _ underscore instead of dash
  const dismissTipInDB = async ({ tipId }) => {
    await HUM.PATCH({
      op: 'replace',
      path: `/home/${tipId}`,
      value: 'true'
    })
  }

  const data = isEmpty(store)
    ? {}
    : {
        loading,
        untilTimeText: occupancyText.getUntilTime({
          occupancy,
          isMultiZone: utils.account.hasCapability('multizone'),
          isNotInUse: false
        }),
        tipModalIn: {
          id: 'modal-occupancy-override-in',
          open: tipModalInOpen,
          tip: true,
          text: getTipModalText({
            zones: store.zones,
            heating,
            schedule: store.schedule,
            occupancyStr: 'IN'
          }),
          onClickTip: utils.settings.setTipDismissed,
          buttons: [
            {
              name: 'ok',
              text: 'OK',
              onClick: () => {
                // if tip is dismissed set it in DB
                if (utils.settings.isTipDismissed('modal-occupancy-override-in')) dismissTipInDB({tipId: "modal_occupancy_override_in"})
                putOccupancyOverride({ targetOccupancy: 'IN' })
                setTipModalInOpen(false)
              }
            },
            { name: 'cancel', text: 'Cancel', onClick: () => setTipModalInOpen(false) }
          ]
        },
        tipModalOut: {
          id: 'modal-occupancy-override-out',
          open: tipModalOutOpen,
          tip: true,
          text: getTipModalText({
            zones: store.zones,
            heating,
            schedule: store.schedule,
            occupancyStr: 'OUT',
            untilTime
          }),
          onClickTip: utils.settings.setTipDismissed,
          buttons: [
            {
              name: 'ok',
              text: 'OK',
              onClick: () => {
                // if tip is dismissed set it in DB
                if (utils.settings.isTipDismissed('modal-occupancy-override-out')) dismissTipInDB({tipId: "modal_occupancy_override_out"})
                setTipModalOutOpen(false)
                putOccupancyOverride({ targetOccupancy: 'OUT', targetUntilTime: untilTime })
              }
            },
            {
              name: 'cancel',
              text: 'Cancel',
              onClick: () => {
                setTipModalOutOpen(false)
              }
            }
          ]
        },
        tipModalAsleep: {
          id: 'modal-occupancy-override-asleep',
          open: tipModalAsleepOpen,
          tip: true,
          text: getTipModalText({
            type: 'OUT-TO-ASLEEP',
            zones: store.zones,
            asleepSetpoint: (heating.zone1.default_setpoints) ? heating.zone1.default_setpoints.asleep.value : 0
          }),
          onClickTip: utils.settings.setTipDismissed,
          buttons: [
            {
              name: 'ok',
              text: 'OK',
              onClick: async () => {
                // if tip is dismissed set it in DB
                if (utils.settings.isTipDismissed('modal-occupancy-override-asleep')) dismissTipInDB({tipId: "modal_occupancy_override_asleep"})
                const calls = store.zones.filter(({type}) => type === 'heating').map(zone => 
                  CPI.DELETE({
                    capability,
                    zone: zone.id,
                    property: 'occupancy_override'
                  })
                )
                await Promise.all(calls)
                setTipModalAsleepOpen(false)
              }
            },
            {
              name: 'cancel',
              text: 'Cancel',
              onClick: () => {
                setTipModalAsleepOpen(false)
              }
            }
          ]
        },
        timeModal: {
          open: timeModalOpen,
          mobiscroll: {
            name: 'modal-occupancy-override-out-time',
            disabled: false,
            value: getMobiscrollValue({ occupancy }),
            onChange: ({ time }) => setUntilTime(convertTimeToUTCDate(time))
          },
          buttons: [
            {
              name: 'ok',
              text: 'OK',
              onClick: () => {
                setTimeModalOpen(false)
                if (utils.settings.isTipDismissed('modal-occupancy-override-out')) {
                  putOccupancyOverride({ targetOccupancy: 'OUT', targetUntilTime: untilTime })
                } else {
                  setTipModalOutOpen(true)
                }
              }
            },
            {
              name: 'cancel',
              text: 'Cancel',
              onClick: () => {
                setTimeModalOpen(false)
              }
            }
          ]
        },
        onClickInOut: async () => {
          const targetOccupancy = occupancy[0].type === 'OUT' ? 'IN' : 'OUT'

          const occupancies = (utils.account.isZigbee())
            ? store.zones.filter(({type}) => type === 'heating').map(zone => utils.schedule.getCurrentDefaultOccupancy({allPeriods: store.schedule[`zone${zone.id}`].allPeriods}))
            : store.zones.map(zone => utils.schedule.getCurrentDefaultOccupancy({allPeriods: store.schedule[`zone${zone.id}`].allPeriods}))

          if (
            occupancies.includes('asleep') &&
            targetOccupancy === 'IN'
          ) {
            setTipModalAsleepOpen(true)
            return
          }

          if (occupancy[0].type === 'VACATION') {
            await CPI.DELETE({
              capability,
              zone: 1,
              property: 'occupancy_away'
            })
            putOccupancyOverride({ targetOccupancy: 'IN' })
            return
          }

          if (!utils.account.hasCapability('multizone') && occupancy[0].type === 'ASLEEP') {
            putOccupancyOverride({ targetOccupancy: 'OUT', targetUntilTime: getInitialUntilTime({ occupancy }) })
            return
          }

          if (targetOccupancy === 'IN') {
            if (utils.settings.isTipDismissed('modal-occupancy-override-in')) {
              putOccupancyOverride({ targetOccupancy: 'IN' })
            } else {
              setTipModalInOpen(true)
            }
          }

          if (targetOccupancy === 'OUT') {
            setTimeModalOpen(true)
          }
        },
        onClickInOutOverrideOut: () => {
          setTimeModalOpen(true)
        }
      }

  return render(data)
}

export default InOutContainer
